import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { collection, addDoc, doc, updateDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Event, EVENT_ZONES, EVENT_ZONE_DESCRIPTIONS } from '../types/Event';
import { Location } from '../types/Location';

interface Props {
  event?: Event | null;
  onClose?: () => void;
  onSave?: () => void;
}

const EventForm = ({ event, onClose, onSave }: Props) => {
  const [formData, setFormData] = useState<Omit<Event, 'id'>>({
    title: '',
    startDate: '',
    endDate: '',
    location: '',
    locationId: '',
    googleMapsUrl: '',
    maxParticipants: 0,
    participants: [],
    zone: EVENT_ZONES.COMMUNAL,

    photoAlbumUrl: ''
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [useKnownLocation, setUseKnownLocation] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      const q = query(collection(db, 'locations'), orderBy('name'));
      const snapshot = await getDocs(q);
      const locs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Location));
      setLocations(locs);
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title,
        startDate: event.startDate,
        endDate: event.endDate,
        location: event.location,
        locationId: event.locationId || '',
        googleMapsUrl: event.googleMapsUrl || '',
        maxParticipants: event.maxParticipants,
        participants: event.participants || [],
        zone: event.zone || EVENT_ZONES.COMMUNAL,
        photoAlbumUrl: event.photoAlbumUrl || ''
      });
      // Si un ID de lieu est présent, on considère que c'est une salle connue
      if (event.locationId) {
        setUseKnownLocation(true);
      } else {
        // Sinon, si c'est un événement communal mais sans ID location, c'est un lieu personnalisé
        setUseKnownLocation(false);
      }
    } else {
      // Par défaut pour un nouvel événement
      setUseKnownLocation(true);
    }
  }, [event]);

  const handleSubmit = async () => {
    try {
      // Pour les événements WAN, définir un nombre maximal élevé de participants (999)
      const updatedFormData = formData.zone === EVENT_ZONES.WAN
        ? { ...formData, maxParticipants: 999 }
        : formData;

      // Vérifier si les participants sont définis
      if (!updatedFormData.participants) {
        updatedFormData.participants = [];
      }

      const eventData = {
        ...updatedFormData,
        isHomeEvent: updatedFormData.zone === EVENT_ZONES.HOME,
        isWanEvent: updatedFormData.zone === EVENT_ZONES.WAN,
        // S'assurer que ces champs sont toujours définis
        participants: updatedFormData.participants || [],
        maxParticipants: updatedFormData.maxParticipants || (updatedFormData.zone === EVENT_ZONES.WAN ? 999 : 10),
        zone: updatedFormData.zone || EVENT_ZONES.COMMUNAL
      };

      // Nettoyage des données selon le mode "Lieu connu"
      if (updatedFormData.zone === EVENT_ZONES.COMMUNAL) {
        if (useKnownLocation) {
          // Si lieu connu, on s'assure que locationId est bien là
          if (!eventData.locationId) {
            alert("Veuillez sélectionner une salle.");
            return;
          }
        } else {
          // Si lieu personnalisé, on vide locationId
          eventData.locationId = '';
        }
      }

      if (event?.id) {
        await updateDoc(doc(db, 'events', event.id), eventData);
      } else {
        await addDoc(collection(db, 'events'), eventData);
      }
      if (onSave) onSave();
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
      <FormControl fullWidth required>
        <InputLabel>Zone*</InputLabel>
        <Select
          value={formData.zone}
          label="Zone*"
          onChange={(e) => {
            const newZone = e.target.value as Event['zone'];
            let newTitle = formData.title;

            // Auto-switch prefix LAN/WAN
            if (newZone === EVENT_ZONES.WAN && newTitle.startsWith('LAN ')) {
              newTitle = newTitle.replace('LAN ', 'WAN ');
            } else if (newZone === EVENT_ZONES.COMMUNAL && newTitle.startsWith('WAN ')) {
              newTitle = newTitle.replace('WAN ', 'LAN ');
            }

            setFormData({
              ...formData,
              zone: newZone,
              title: newTitle
            });
          }}
          fullWidth
        >
          {Object.entries(EVENT_ZONE_DESCRIPTIONS).map(([value, description]) => (
            <MenuItem key={value} value={value}>
              {description}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Titre*"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        required
        fullWidth
      />
      <TextField
        label="Date de début*"
        type="datetime-local"
        value={formData.startDate}
        onChange={(e) => {
          const newStartDate = e.target.value;
          if (newStartDate) {
            const date = new Date(newStartDate);
            date.setDate(date.getDate() + 1);
            date.setHours(2, 0, 0, 0);

            // Format manually to match datetime-local input requirement: YYYY-MM-DDThh:mm
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const formattedEndDate = `${year}-${month}-${day}T${hours}:${minutes}`;

            setFormData({
              ...formData,
              startDate: newStartDate,
              endDate: formattedEndDate
            });
          } else {
            setFormData({ ...formData, startDate: newStartDate });
          }
        }}
        required
        InputLabelProps={{ shrink: true }}
        fullWidth
      />
      <TextField
        label="Date de fin*"
        type="datetime-local"
        value={formData.endDate}
        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
        required
        InputLabelProps={{ shrink: true }}
        fullWidth
      />
      {formData.zone === EVENT_ZONES.COMMUNAL ? (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Dans une salle connue ?</InputLabel>
            <Select
              value={useKnownLocation ? "yes" : "no"}
              label="Dans une salle connue ?"
              onChange={(e) => {
                const isKnown = e.target.value === "yes";
                setUseKnownLocation(isKnown);
                // Reset location fields when switching
                setFormData({
                  ...formData,
                  location: '',
                  locationId: '',
                  googleMapsUrl: ''
                });
              }}
            >
              <MenuItem value="yes">Oui</MenuItem>
              <MenuItem value="no">Non</MenuItem>
            </Select>
          </FormControl>

          {useKnownLocation ? (
            <FormControl fullWidth required>
              <InputLabel>Lieu*</InputLabel>
              <Select
                value={formData.locationId || ''}
                label="Lieu*"
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedLoc = locations.find(l => l.id === selectedId);
                  if (selectedLoc) {
                    setFormData({
                      ...formData,
                      location: selectedLoc.name,
                      locationId: selectedLoc.id,
                      googleMapsUrl: selectedLoc.googleMapsUrl,
                      // Auto-remplissage du nombre de participants max si défini dans la salle
                      maxParticipants: selectedLoc.maxPlayers || formData.maxParticipants
                    });
                  }
                }}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nom du lieu*"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                fullWidth
                placeholder="Ex: Salle des fêtes de..."
              />
              <TextField
                label="Lien Google Maps*"
                value={formData.googleMapsUrl}
                onChange={(e) => setFormData({ ...formData, googleMapsUrl: e.target.value })}
                required
                fullWidth
                placeholder="https://maps.app.goo.gl/..."
              />
            </Box>
          )}
        </>
      ) : (
        <TextField
          label={formData.zone === EVENT_ZONES.WAN ? "Lien Discord*" : "Lieu*"}
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder={formData.zone === EVENT_ZONES.WAN ? "https://discord.gg/..." : ""}
          helperText={formData.zone === EVENT_ZONES.WAN ? "Collez ici le lien d'invitation Discord" : ""}
          required
          fullWidth
        />
      )}
      {/* Masquer le champ de nombre maximum de participants pour les événements WAN */}
      {formData.zone !== EVENT_ZONES.WAN && (
        <TextField
          label="Nombre maximum de participants*"
          type="number"
          value={formData.maxParticipants}
          onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) })}
          required
          fullWidth
        />
      )}

      <TextField
        label="Lien album photos (Google Photos)"
        value={formData.photoAlbumUrl}
        onChange={(e) => setFormData({ ...formData, photoAlbumUrl: e.target.value })}
        placeholder="https://photos.google.com/share/..."
        helperText="Collez ici le lien de partage de l'album Google Photos"
        fullWidth
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button onClick={onClose} color="error">
          Annuler
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {event?.id ? 'Modifier' : 'Créer'}
        </Button>
      </Box>
    </Box>
  );
};

export default EventForm;
